import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { BASE_URL } from "./config";

// type Token = any;
// /**
//  * A function that, given an access token, will modify an axios request
//  * config and re-run the request.
//  */
// type RequestCallback = (token: Token) => void;
// type PendingRequests = RequestCallback[];

export type ApiErrorMessage = {
  field_name: string;
  message_code: string;
};

interface AvamaeResponse<T, E> {
  id: number;
  details: T;
  status: "1" | "0";
  errors: E;
}

/**
 * Only to be used by `refreshTokens`.
 * For all other API requests, use types available from `this.makeRequest`
 */
export type RefreshAPIResponse<T = unknown, E = any> = {
  data: AvamaeResponse<T, E>;
  config: AxiosResponse["config"];
};

type ApiResponseType<D, E, R> = {
  data: D | null;
  errors: E | null;
  success: boolean;
  wasCancelled: boolean;
  cancelReason: R | null;
  requestPayload: AxiosRequestConfig["params"];
};

type MakeRequestArgs<P> = {
  url: string;
  method: "GET" | "POST";
  payload?: P;
  config?: AxiosRequestConfig;
};

/**
 * ## BaseApi ##
 *
 * This is the lowest common denominator for **all** `*Api.ts` `class` files.
 */
export class BaseApi {
  /**
   * A generic message for when the request failed for an unknown reason.
   */
  public static UNKNOWN_ERROR_OCCURRED =
    "An unknown error occurred! Please try again later.";
  /**
   * A generic message for when the request failed for an unknown reason.
   */
  public static INVALID_ACCESS_MESSAGE =
    "Sorry, we ran into an error authorising your request. Please try signing out and back in again.";

  /**
   * A generic message for when the request was cancelled using the `AbortController`
   */
  public static REQUEST_CANCELLED =
    "The request was cancelled! Please try again later";

  /**
   * Checks that the API response was successful
   *
   * @param response
   * @returns Boolean
   */
  public static isResponseSuccessful = (
    response: AxiosResponse<AvamaeResponse<unknown, unknown>>
  ): boolean => response.data.status === "1";

  // TODO: An `AbortController` wrapper function
  /**
   * Makes a request based on the data provided.
   *
   * @required url
   * @required method
   * @optional payload - Applies only to `method: "POST"` requests
   * @optional config - Request config for things such as (but not limited to)
   *  - Query String parameters
   *  - AbortControllers (https://axios-http.com/docs/cancellation)
   *  **See `AxiosRequestConfig` type for more information**
   *
   * @returns
   */
  public static makeRequest = async <T, E = any, P = Record<any, any>>({
    url,
    method,
    payload,
    config,
  }: MakeRequestArgs<P>) => {
    if (method === "POST") {
      return await instance.post<
        AvamaeResponse<T, E>,
        AxiosResponse<AvamaeResponse<T, E>>,
        P
      >(url, payload, config);
    }
    if (payload && method === "GET") {
      console.warn(
        `GET requests (url: "${url}") cannot carry a payload. Send params in config instead`
      );
    }
    return await instance.get<
      AvamaeResponse<T, E>,
      AxiosResponse<AvamaeResponse<T, E>>,
      P
    >(url, config);
  };

  /**
   *
   * @param data Response data
   * @returns A Success response object with the
   */
  public static successResponse<D>(
    data: D,
    requestPayload?: AxiosRequestConfig["params"]
  ): ApiResponseType<D, null, null> {
    return {
      data,
      errors: null,
      success: true,
      wasCancelled: false,
      cancelReason: null,
      requestPayload,
    };
  }

  /**
   *
   * @param errors Error reasons for why the response failed
   * @returns An Error response object with error details
   */
  public static errorResponse<E>(
    errors: E,
    requestPayload?: AxiosRequestConfig["params"]
  ): ApiResponseType<null, E, null> {
    return {
      data: null,
      errors,
      success: false,
      wasCancelled: false,
      cancelReason: null,
      requestPayload,
    };
  }

  private static getReason<R>(
    abortControllers: (AbortController | null)[],
    fallBackReason = this.REQUEST_CANCELLED
  ): R {
    let reason = fallBackReason;
    let reasonFound = false;
    abortControllers.forEach((controller) => {
      if (reasonFound) {
        return;
      }
      if (
        controller &&
        !!controller.signal.aborted &&
        !!controller.signal.reason
      ) {
        reason = controller.signal.reason;
        reasonFound = true;
      }
    });
    return reason as R;
  }

  /**
   *
   * @param reason Reason for why the response was cancelled
   * @returns A deliberate Error response object where `wasCancelled: true`
   */
  public static cancelledResponse<R = string>(
    abortControllers: (AbortController | null)[],
    fallBackReason = this.REQUEST_CANCELLED,
    requestPayload?: AxiosRequestConfig["params"]
  ): ApiResponseType<null, null, R> {
    return {
      data: null,
      errors: null,
      success: false,
      wasCancelled: true,
      cancelReason: this.getReason<R>(abortControllers, fallBackReason),
      requestPayload,
    };
  }

  public static parseGenericErrorMessageCode(
    msg?: ApiErrorMessage["message_code"]
  ) {
    switch (msg) {
      case "Invalid_Pano_Guid":
        return "Invalid panorama GUID";
      case "Invalid_Field_ID":
      case "Invalid_Id":
        return "Invalid field ID";
      case "No_Image_For_Field":
        return "No panorama image";
      case "Invalid_Token":
        return "Invalid token";
      case "Invalid_Access":
        return this.INVALID_ACCESS_MESSAGE;
      default:
        return this.UNKNOWN_ERROR_OCCURRED;
    }
  }

  /**
   * TypeGuard to check if a caught error is an Axios error
   * Allows type safety in the `catch` block
   */
  private static checkIfAxios<T, E>(
    error: unknown
  ): error is AxiosError<AvamaeResponse<T, E>> {
    return axios.isAxiosError(error);
  }

  public static safelyCatchUnknownError<T>(
    caughtError: unknown,
    origin: string,
    parser: typeof this.parseGenericErrorMessageCode
  ) {
    try {
      // Attempt to see if Axios threw an error with a response
      if (this.checkIfAxios<T, ApiErrorMessage[]>(caughtError)) {
        return this.errorResponse(
          parser(caughtError.response?.data.errors[0].message_code)
        );
      }
      // Else fallback to generic error handling
      console.warn("Unknown error occurred in:", origin, caughtError);
      return this.errorResponse(this.UNKNOWN_ERROR_OCCURRED);
    } catch (err: unknown) {
      console.error("Further unknown error occurred in:", origin, err);
      return this.errorResponse(this.UNKNOWN_ERROR_OCCURRED);
    }
  }
}

/**
 * Custom axios instance with base url set, and some interceptors to
 * handle auth headers.
 *
 */
const instance = axios.create({
  baseURL: BASE_URL,
});

export default instance;
